import { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ErrorToast } from '../../../adminComponents/Toasts/Toasts';
import {
  ChatCropIcon,
  ChatDrawIcon,
  ChatRedoIcon,
  ChatRestoreIcon,
  ChatSendIcon,
  ChatTextIcon,
  ChatTrashIcon,
  X,
} from '../../../assets/svg';
import { reportError } from '../../../helpers/reportError';
import { lightTheme } from '../../../styles/theme';
import {
  ComposerBar,
  ComposerTextarea,
  SendButton,
} from '../chatSurfaces.styled';
import { useEditorHistory } from '../hooks/useEditorHistory';
import { usePointerSeek } from '../hooks/usePointerSeek';
import { MAX_BODY_LENGTH } from './ChatInput';
import {
  brushSizeFor,
  DEFAULT_BRUSH_T,
  DEFAULT_COLOR_T,
  EditorCrop,
  EditorState,
  EditorStroke,
  EditorText,
  EMPTY_EDITOR_STATE,
  exportEditedImage,
  hitTestText,
  isFullCrop,
  isPristine,
  loadImageElement,
  measureTextWidth,
  normalizeCrop,
  renderEditorState,
  renderStroke,
  sliderColor,
  SLIDER_TRACK_GRADIENT,
  textSizeFor,
} from './imageEditorCore';
import {
  Backdrop,
  CanvasBox,
  CloseButton,
  ColorThumb,
  ColorTrack,
  Controls,
  EditCanvas,
  EditorTool,
  SizeThumb,
  SizeTrack,
  Stage,
  TextEditInput,
  ToolButton,
  ToolDivider,
  ToolRow,
} from './ImageEditor.styled';
import { ImageEditorCrop } from './ImageEditorCrop';

type Props = {
  /** The picked image; already validated against the upload limits. */
  file: File;
  /** Discards the draft. Blocked while the send is in flight. */
  onClose: () => void;
  /**
   * Hands the finished image over. Resolves true once the message is IN the
   * conversation - not once it has reached the server: the upload runs behind
   * the bubble it put there, which is what lets this modal close straight away
   * rather than holding a spinner over a picture that is already decided. The
   * parent unmounts this on true; false keeps it open with everything intact.
   */
  onSend: (image: File, caption: string) => Promise<boolean>;
};

const sameCrop = (a: EditorCrop | null, b: EditorCrop | null) =>
  a === b ||
  (!!a &&
    !!b &&
    a.x === b.x &&
    a.y === b.y &&
    a.width === b.width &&
    a.height === b.height);

/**
 * Folds the text currently being typed into a state, exactly as committing it
 * would. Pure and called from two places - the commit itself, and the send
 * path, which cannot wait for a state update to land before exporting.
 * Returns `base` UNCHANGED when there is nothing to record, so callers can
 * use reference equality to skip an empty undo step.
 */
const mergeEditingText = (base: EditorState, editing: EditorText): EditorState => {
  const value = editing.value.trim();
  const current = base.texts.find((text) => text.id === editing.id);

  if (!value) {
    if (!current) return base;
    return { ...base, texts: base.texts.filter((t) => t.id !== editing.id) };
  }
  if (!current) return { ...base, texts: [...base.texts, { ...editing, value }] };
  if (current.value === value && current.color === editing.color) return base;
  return {
    ...base,
    texts: base.texts.map((t) => (t.id === editing.id ? { ...editing, value } : t)),
  };
};

/**
 * The draft screen between picking an image and sending it: crop, freehand
 * drawing and text, with undo/redo/reset, a colour pill and a caption - the
 * Telegram flow, reduced to what this chat needs.
 *
 * All edit geometry lives in IMAGE pixels (see imageEditorCore); this
 * component owns the mapping to screen pixels and the pointer state machine,
 * and `useEditorHistory` owns what "undo" means. Nothing here talks to the
 * network - the parent's `onSend` does, so the editor works the same against
 * any conversation kind.
 */
export const ImageEditor = ({ file, onClose, onSend }: Props) => {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [tool, setTool] = useState<EditorTool | null>(null);
  const [colorT, setColorT] = useState(DEFAULT_COLOR_T);
  /** Where the brush-size slider sits; 0..1, turned into pixels per picture. */
  const [brushT, setBrushT] = useState(DEFAULT_BRUSH_T);
  const [caption, setCaption] = useState('');
  const [busy, setBusy] = useState(false);

  const history = useEditorHistory<EditorState>(EMPTY_EDITOR_STATE);
  const { state } = history;

  /** The stroke under the pointer right now - not in history until release. */
  const [draftStroke, setDraftStroke] = useState<EditorStroke | null>(null);
  /** The crop rect being adjusted while the crop tool is open. */
  const [draftCrop, setDraftCrop] = useState<EditorCrop | null>(null);
  /** The text being typed; its DOM input stands in for it on the canvas. */
  const [editingText, setEditingText] = useState<EditorText | null>(null);
  /** A text mid-drag: rendered at the preview spot, committed on release. */
  const [dragPreview, setDragPreview] = useState<{
    id: number;
    x: number;
    y: number;
  } | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [stageSize, setStageSize] = useState<{ w: number; h: number } | null>(
    null
  );
  const textDragRef = useRef<{
    id: number;
    start: { x: number; y: number };
    origin: { x: number; y: number };
    moved: boolean;
  } | null>(null);
  /** One counter for strokes and texts alike, so no id is ever reused. */
  const nextIdRef = useRef(1);

  // In a ref so a parent re-render (onClose is usually an inline arrow) can
  // never re-run the decode effect below - it is keyed on the file alone.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  /* Decode the picked file once. The object URL lives until unmount - the
   * <img> keeps its decoded bitmap either way, but Safari has been flaky
   * about drawing from an img whose URL was revoked mid-life. */
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    loadImageElement(file)
      .then(({ image: element, url }) => {
        objectUrl = url;
        if (!cancelled) setImage(element);
      })
      .catch(() => {
        ErrorToast(`Couldn't open ${file.name}.`);
        onCloseRef.current();
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  /* The stage is whatever is left between the close button and the controls;
   * the canvas fits the picture inside it, so its size has to be live. */
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0].contentRect;
      setStageSize({ w: rect.width, h: rect.height });
    });
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);

  /**
   * What part of the image is on screen. Cropped view normally, the FULL
   * image while the crop tool is open - adjusting a frame you can only see
   * the inside of is guesswork.
   */
  const view = useMemo<EditorCrop | null>(() => {
    if (!image) return null;
    if (tool !== 'crop' && state.crop) return state.crop;
    return { x: 0, y: 0, width: image.naturalWidth, height: image.naturalHeight };
  }, [image, tool, state.crop]);

  /** Display pixels per image pixel. */
  const scale =
    view && stageSize
      ? Math.min(stageSize.w / view.width, stageSize.h / view.height)
      : 0;

  /* Keep the crop draft in step with history - entering the tool, undo, redo
   * and reset all land here rather than each patching the draft itself. */
  useEffect(() => {
    if (tool !== 'crop' || !image) return;
    setDraftCrop(
      state.crop ?? {
        x: 0,
        y: 0,
        width: image.naturalWidth,
        height: image.naturalHeight,
      }
    );
  }, [tool, image, state.crop]);

  /* Repaint. One render path for everything the user sees: the committed
   * state, the stroke mid-draw, a text mid-drag - all through the same
   * transform, so the preview cannot disagree with the export. */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !image || !view || !scale) return;

    const dpr = window.devicePixelRatio || 1;
    const cssWidth = view.width * scale;
    const cssHeight = view.height * scale;
    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;
    canvas.width = Math.max(1, Math.round(cssWidth * dpr));
    canvas.height = Math.max(1, Math.round(cssHeight * dpr));

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(
      scale * dpr,
      0,
      0,
      scale * dpr,
      -view.x * scale * dpr,
      -view.y * scale * dpr
    );

    const texts = dragPreview
      ? state.texts.map((text) =>
          text.id === dragPreview.id
            ? { ...text, x: dragPreview.x, y: dragPreview.y }
            : text
        )
      : state.texts;

    renderEditorState(ctx, image, { ...state, texts }, editingText?.id ?? null);
    if (draftStroke) renderStroke(ctx, draftStroke);
  }, [image, view, scale, state, draftStroke, dragPreview, editingText]);

  /* ---------------------------------------------------------------------- */
  /* Pointer handling on the canvas (draw and text; crop has its own        */
  /* overlay)                                                               */
  /* ---------------------------------------------------------------------- */

  const pointFromEvent = (event: React.PointerEvent) => {
    const canvas = canvasRef.current;
    if (!canvas || !view || !scale) return null;
    const rect = canvas.getBoundingClientRect();
    return {
      x: view.x + (event.clientX - rect.left) / scale,
      y: view.y + (event.clientY - rect.top) / scale,
    };
  };

  const commitEditingText = () => {
    if (!editingText) return;
    const next = mergeEditingText(state, editingText);
    if (next !== state) history.commit(next);
    setEditingText(null);
  };

  const onCanvasPointerDown = (event: React.PointerEvent) => {
    if (busy || !image) return;
    /*
     * Cancelling the pointerdown suppresses its default focus action. Without
     * this, placing a text goes: the handler mounts the autofocused input,
     * THEN the browser's own default for the same press moves focus to the
     * body (a canvas is not focusable) - blurring the brand-new input, whose
     * empty commit removes it before a single letter lands.
     */
    event.preventDefault();
    const point = pointFromEvent(event);
    if (!point) return;

    if (tool === 'draw') {
      event.currentTarget.setPointerCapture(event.pointerId);
      setDraftStroke({
        id: nextIdRef.current++,
        color: sliderColor(colorT),
        size: brushSizeFor(image, brushT),
        points: [point],
      });
      return;
    }

    if (tool === 'text') {
      // A press while typing only closes that edit - the press that places
      // the NEXT text is the one after, or two texts appear per misclick.
      if (editingText) {
        commitEditingText();
        return;
      }

      const hit = hitTestText(state.texts, point);
      if (hit) {
        event.currentTarget.setPointerCapture(event.pointerId);
        textDragRef.current = {
          id: hit.id,
          start: point,
          origin: { x: hit.x, y: hit.y },
          moved: false,
        };
        return;
      }

      setEditingText({
        id: nextIdRef.current++,
        value: '',
        x: Math.min(Math.max(point.x, 0), image.naturalWidth),
        y: Math.min(Math.max(point.y, 0), image.naturalHeight),
        color: sliderColor(colorT),
        size: textSizeFor(image),
      });
    }
  };

  const onCanvasPointerMove = (event: React.PointerEvent) => {
    const point = pointFromEvent(event);
    if (!point) return;

    if (draftStroke) {
      setDraftStroke((stroke) =>
        stroke ? { ...stroke, points: [...stroke.points, point] } : stroke
      );
      return;
    }

    const drag = textDragRef.current;
    if (drag) {
      const dx = point.x - drag.start.x;
      const dy = point.y - drag.start.y;
      // Under the threshold it is still a click; past it, a move. In image
      // pixels the threshold scales with zoom, so divide back by scale.
      if (!drag.moved && Math.hypot(dx, dy) < 4 / scale) return;
      drag.moved = true;
      setDragPreview({
        id: drag.id,
        x: drag.origin.x + dx,
        y: drag.origin.y + dy,
      });
    }
  };

  const onCanvasPointerUp = () => {
    if (draftStroke) {
      history.commit({ ...state, strokes: [...state.strokes, draftStroke] });
      setDraftStroke(null);
    }

    const drag = textDragRef.current;
    if (drag) {
      textDragRef.current = null;
      if (drag.moved && dragPreview) {
        history.commit({
          ...state,
          texts: state.texts.map((text) =>
            text.id === drag.id
              ? { ...text, x: dragPreview.x, y: dragPreview.y }
              : text
          ),
        });
      } else {
        // A click without a move reopens the text for typing.
        const text = state.texts.find((t) => t.id === drag.id);
        if (text) setEditingText({ ...text });
      }
      setDragPreview(null);
    }
  };

  const onCanvasPointerCancel = () => {
    setDraftStroke(null);
    textDragRef.current = null;
    setDragPreview(null);
  };

  /* ---------------------------------------------------------------------- */
  /* Tools, colour, history                                                  */
  /* ---------------------------------------------------------------------- */

  const toggleTool = (next: EditorTool) => {
    if (busy) return;
    if (editingText) commitEditingText();
    setTool((current) => (current === next ? null : next));
  };

  const commitCrop = () => {
    if (!draftCrop || !image) return;
    const normalized = normalizeCrop(
      draftCrop,
      image.naturalWidth,
      image.naturalHeight
    );
    const next = isFullCrop(normalized, image.naturalWidth, image.naturalHeight)
      ? null
      : normalized;
    // A click on the frame that moved nothing should not burn an undo step.
    if (!sameCrop(next, state.crop)) history.commit({ ...state, crop: next });
  };

  const colorTrackRef = useRef<HTMLDivElement | null>(null);
  // The voice players' scrubber hook, seeking a 1-second "track": the seconds
  // it reports back ARE the 0..1 fraction along the pill.
  const { seekHandlers } = usePointerSeek({
    trackRef: colorTrackRef,
    duration: 1,
    onSeek: (t) => {
      setColorT(t);
      setEditingText((editing) =>
        editing ? { ...editing, color: sliderColor(t) } : editing
      );
    },
  });

  const sizeTrackRef = useRef<HTMLDivElement | null>(null);
  const { seekHandlers: sizeHandlers } = usePointerSeek({
    trackRef: sizeTrackRef,
    duration: 1,
    onSeek: setBrushT,
  });

  const undo = () => {
    if (editingText) commitEditingText();
    history.undo();
  };
  const redo = () => {
    if (editingText) commitEditingText();
    history.redo();
  };
  const reset = () => {
    if (editingText) setEditingText(null);
    history.reset();
  };

  /* Escape backs out one layer at a time - text edit, then tool, then the
   * whole editor - so one stray press cannot throw a drawing away. */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const inField =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement;

      if (event.key === 'Escape') {
        if (busy || inField) return; // fields handle their own Escape
        if (editingText) return commitEditingText();
        if (tool) return setTool(null);
        return onClose();
      }

      if (inField || busy) return;
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) redo();
        else undo();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // No dependency array on purpose: the handler reads half the component's
    // state, and re-attaching one listener per render is cheaper to reason
    // about than keeping that list honest forever.
  });

  /* ---------------------------------------------------------------------- */
  /* Caption + send                                                          */
  /* ---------------------------------------------------------------------- */

  const onCaptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCaption(event.target.value);
    // The composer's auto-grow, in miniature: reset then take the scroll
    // height, capped at the textarea's own max-height.
    event.target.style.height = 'auto';
    event.target.style.height = `${Math.min(event.target.scrollHeight, 120)}px`;
  };

  const handleSend = async () => {
    if (busy || !image) return;

    // Fold an open text edit in synchronously - a state update would not be
    // visible to this closure until the next render, after the export.
    const finalState = editingText
      ? mergeEditingText(state, editingText)
      : state;
    if (editingText) {
      if (finalState !== state) history.commit(finalState);
      setEditingText(null);
    }

    setBusy(true);
    try {
      const exported = await exportEditedImage(image, finalState, file);
      const sent = await onSend(exported, caption.trim());
      // On success the parent unmounts this modal; only failure comes back.
      if (!sent) setBusy(false);
    } catch (error) {
      setBusy(false);
      reportError(error, { fallbackMessage: "Couldn't prepare the image." });
    }
  };

  const onCaptionKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  /* ---------------------------------------------------------------------- */

  const modalRoot =
    typeof document === 'undefined'
      ? null
      : document.getElementById('modal-root');
  if (!modalRoot) return null;

  const showColorPill = tool === 'draw' || tool === 'text';
  const activeColor = sliderColor(colorT);
  /* The thumb IS the preview, so it grows with the setting - across the same
     range the wedge behind it is drawn at, which is what lets the two line up
     at both ends of the track. */
  const thumbPreviewPx = Math.round(8 + brushT * 18);

  return ReactDOM.createPortal(
    <Backdrop>
      <CloseButton
        type="button"
        aria-label="Discard image"
        onClick={onClose}
        disabled={busy}
      >
        <X fill={lightTheme.primary} />
      </CloseButton>

      {/* No spinner while the picture decodes, and none while it exports.
          Both are local work on a file the user just picked - a few frames -
          and a spinner over an empty stage only draws attention to a wait that
          is over before it registers. The send is not waited on here at all
          any more: it puts the message in the conversation and the upload runs
          behind that bubble. */}
      <Stage ref={stageRef}>
        {image && view && scale > 0 && (
          <CanvasBox>
            <EditCanvas
              ref={canvasRef}
              $tool={tool}
              onPointerDown={onCanvasPointerDown}
              onPointerMove={onCanvasPointerMove}
              onPointerUp={onCanvasPointerUp}
              onPointerCancel={onCanvasPointerCancel}
            />

            {tool === 'crop' && draftCrop && (
              <ImageEditorCrop
                crop={draftCrop}
                scale={scale}
                imageWidth={image.naturalWidth}
                imageHeight={image.naturalHeight}
                onChange={setDraftCrop}
                onCommit={commitCrop}
              />
            )}

            {editingText && (
              <TextEditInput
                autoFocus
                value={editingText.value}
                placeholder="Text"
                maxLength={200}
                style={{
                  left: (editingText.x - view.x) * scale,
                  top: (editingText.y - view.y) * scale,
                  fontSize: editingText.size * scale,
                  color: editingText.color,
                  width:
                    Math.max(
                      measureTextWidth(
                        editingText.value || 'Text',
                        editingText.size
                      ) * scale,
                      32
                    ) + 24,
                }}
                onChange={(event) =>
                  setEditingText((editing) =>
                    editing
                      ? { ...editing, value: event.target.value }
                      : editing
                  )
                }
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    commitEditingText();
                  }
                  if (event.key === 'Escape') {
                    event.stopPropagation();
                    setEditingText(null); // discard, keep the original
                  }
                }}
                onBlur={commitEditingText}
              />
            )}
          </CanvasBox>
        )}
      </Stage>

      <Controls>
        {showColorPill && (
          <ColorTrack
            ref={colorTrackRef}
            aria-label="Colour"
            style={{ background: SLIDER_TRACK_GRADIENT }}
            {...seekHandlers}
          >
            <ColorThumb
              style={{
                left: `calc(${colorT} * (100% - 26px) + 13px)`,
                background: activeColor,
              }}
            />
          </ColorTrack>
        )}

        {/* Drawing only. Text has its own size, taken from the picture, and a
            second meaning for the same bar would be one control saying two
            things depending on which tool happens to be active. */}
        {tool === 'draw' && (
          <SizeTrack ref={sizeTrackRef} aria-label="Brush size" {...sizeHandlers}>
            <SizeThumb
              style={{
                left: `calc(${brushT} * (100% - 26px) + 13px)`,
                width: thumbPreviewPx,
                height: thumbPreviewPx,
                background: activeColor,
              }}
            />
          </SizeTrack>
        )}

        <ToolRow>
          <ToolButton
            type="button"
            aria-label="Crop"
            title="Crop"
            $active={tool === 'crop'}
            onClick={() => toggleTool('crop')}
            disabled={busy}
          >
            <ChatCropIcon
              color={tool === 'crop' ? lightTheme.white : undefined}
            />
          </ToolButton>
          <ToolButton
            type="button"
            aria-label="Draw"
            title="Draw"
            $active={tool === 'draw'}
            onClick={() => toggleTool('draw')}
            disabled={busy}
          >
            <ChatDrawIcon
              color={tool === 'draw' ? lightTheme.white : undefined}
            />
          </ToolButton>
          <ToolButton
            type="button"
            aria-label="Add text"
            title="Add text"
            $active={tool === 'text'}
            onClick={() => toggleTool('text')}
            disabled={busy}
          >
            <ChatTextIcon
              color={tool === 'text' ? lightTheme.white : undefined}
            />
          </ToolButton>
          <ToolDivider />

          <ToolButton
            type="button"
            aria-label="Undo"
            title="Undo"
            onClick={undo}
            disabled={busy || (!history.canUndo && !editingText)}
          >
            <ChatRestoreIcon />
          </ToolButton>
          <ToolButton
            type="button"
            aria-label="Redo"
            title="Redo"
            onClick={redo}
            disabled={busy || !history.canRedo}
          >
            <ChatRedoIcon />
          </ToolButton>
          <ToolButton
            type="button"
            aria-label="Reset all edits"
            title="Reset all edits"
            onClick={reset}
            disabled={busy || (isPristine(state) && !editingText)}
          >
            <ChatTrashIcon />
          </ToolButton>
        </ToolRow>

        <ComposerBar>
          <ComposerTextarea
            rows={1}
            value={caption}
            maxLength={MAX_BODY_LENGTH}
            placeholder="Add a caption"
            disabled={busy}
            onChange={onCaptionChange}
            onKeyDown={onCaptionKeyDown}
          />
          <SendButton
            type="button"
            aria-label="Send image"
            onClick={handleSend}
            disabled={busy || !image}
          >
            {/* Always white - the disc under it is what changes colour. */}
            <ChatSendIcon color={lightTheme.white} />
          </SendButton>
        </ComposerBar>
      </Controls>
    </Backdrop>,
    modalRoot
  );
};
